import { from, Observable } from 'rxjs';
import * as CryptoJS from 'crypto-js';

import { BaseService, IConfigService, ILoggingService, IModelEntity, UtilsMisc, UtilsString } from '@pit/core';
import { ILocalStorageProvider } from '../interfaces/local.storage.provider.interface';

export class LocalStorageProviderBase<T extends IModelEntity> extends BaseService implements ILocalStorageProvider<T> {

    protected _entityName: string = null;

    constructor(configurationService: IConfigService, loggingService: ILoggingService, entityName: string) {
        super(configurationService, loggingService);

        this._entityName = entityName;
    }

    public cryptoKey: string = null;

    public get entityName(): string {
        return this._entityName;
    }

    public getCount$(): Observable<number> {
        return this.doGetCount$();
    }

    public async getCountAsync(): Promise<number> {
        return this.doGetCountAsync();
    }

    public clear(): void {
        this.doClear();
    }

    public delete$(entity: T): Observable<T> {
        return this.doDelete$(entity);
    }

    public getAll$(): Observable<T[]> {
        return this.doGetAll$();
    }

    public getById$(id: number): Observable<T> {
        return this.doGetById$(id);
    }

    public getIdExists$(id: number): Observable<boolean> {
        return this.doGetIdExists$(id);
    }

    public save$(entity: T): Observable<T> {
        return this.doSave$(entity);
    }

    public saveMulti(entities: T[]): void {
        this.doSaveMulti(entities);
    }

    public async clearAsync(): Promise<void> {
        return this.doClearAsync();
    }

    public async deleteAsync(entity: T): Promise<T> {
        return this.doDeleteAsync(entity);
    }

    public async getAllAsync(): Promise<T[]> {
        return this.doGetAllAsync();
    }

    public async getByIdAsync(id: number): Promise<T> {
        return this.doGetByIdAsync(id);
    }

    public async getElementAtAsync(index: number): Promise<T> {
        return this.doGetElementAtAsync(index);
    }

    public async getIdExistsAsync(id: number): Promise<boolean> {
        return this.doGetIdExistsAsync(id);
    }

    public async saveAsync(entity: T): Promise<T> {
        return this.doSaveAsync(entity);
    }

    public async saveMultiAsync(entities: T[]): Promise<void> {
        await this.doSaveMultiAsync(entities);
    }

    public syncSavedItemsToRemote(): boolean {
        return this.doSyncSavedItemsToRemote();
    }

    public async syncSavedItemsToRemoteAsync(): Promise<boolean> {
        return this.doSyncSavedItemsToRemoteAsync();
    }

    protected doGetCount$(): Observable<number> {
        return from(this.doGetCountAsync());
    }

    protected doClear(): void {
        this.clearAsync();
    }

    protected doDelete$(entity: T): Observable<T> {
        return from(this.doDeleteAsync(entity));
    }

    protected doGetAll$(): Observable<T[]> {
        return from(this.doGetAllAsync());
    }

    protected doGetById$(id: number): Observable<T> {
        return from(this.doGetByIdAsync(id));
    }

    protected doGetIdExists$(id: number): Observable<boolean> {
        return from(this.doGetIdExistsAsync(id));
    }

    protected doSave$(entity: T): Observable<T> {
        return from(this.doSaveAsync(entity));
    }

    protected doSaveMulti(entities: T[]): void {
        this.doSaveMultiAsync(entities);
    }

    protected async doGetCountAsync(): Promise<number> {
        return 0;
    }

    protected async doClearAsync(): Promise<void> {
    }

    protected async doDeleteAsync(entity: T): Promise<T> {
        return entity;
    }

    protected async doGetAllAsync(): Promise<T[]> {
        return null;
    }

    protected async doGetByIdAsync(id: number): Promise<T> {
        return null;
    }

    protected async doGetElementAtAsync(index: number): Promise<T> {
        return null;
    }

    protected async doGetIdExistsAsync(id: number): Promise<boolean> {
        return false;
    }

    protected async doSaveAsync(entity: T): Promise<T> {
        return entity;
    }

    protected async doSaveMultiAsync(entities: T[]): Promise<void> {
    }

    protected doSyncSavedItemsToRemote(): boolean {
        return false;
    }

    protected async doSyncSavedItemsToRemoteAsync(): Promise<boolean> {
        return false;
    }

    protected createEntityFromJson(json: any): T {
        return null;
    }

    protected getStorageKey(id: number): string {
        return UtilsString.stringFormat('{0}->[{1}]', this.entityName, id);
    }

    protected decryptData(data: string): any {
        let result: any = data;

        if ((UtilsString.stringIsNullOrEmpty(this.cryptoKey) === false) && (UtilsString.stringIsNullOrEmpty(data) === false)) {
            const decryptResult = CryptoJS.AES.decrypt(result, this.cryptoKey);
            result = JSON.parse(decryptResult.toString(CryptoJS.enc.Utf8));
        }

        return result;
    }

    protected encryptData(data: any): any {
        let result: any = data;

        if ((UtilsString.stringIsNullOrEmpty(this.cryptoKey) === false) && (UtilsMisc.isNullOrUndefined(data) === false)) {
            result = CryptoJS.AES.encrypt(JSON.stringify(data), this.cryptoKey).toString();
        }

        return result;
    }

}
